import strformat from 'string-format';
import qstr from 'querystring';
import moment from 'moment-timezone';
import { ConsoleLog } from './consoleLog';

const logger = new ConsoleLog();


/**
 * Keywords that will be replaced in URL template for report email
 * @type {string[]}
 */
export const TemplateKeywords = [
  'startDt',
  'endDt',
];

/**
 * Query string keys that will be replaced in URL template for report email
 */
const templateKeyParams = TemplateKeywords.reduce((obj, key) => {
  // eslint-disable-next-line no-param-reassign
  obj[key] = `{${key}}`;
  return obj;
}, {});

/**
 * Replace relevant query string variables in a URL to make a URL template for a report
 * @param url
 * @returns {string} template
 */
export function createUrlTemplate(url) {
  const [urlPath, urlSearch] = url.split('?');
  const urlQueryStringParams = qstr.parse(urlSearch || '');
  const { timeFrameId } = urlQueryStringParams;
  urlQueryStringParams.timeFrameId = 'customDateRange';
  // VCCM-2073 if already customDateRange do not overwrite with template
  const templateQueryStringParams = { ...urlQueryStringParams, ...(timeFrameId !== 'customDateRange' ? templateKeyParams : {}) };
  return `${urlPath}?${qstr.stringify(templateQueryStringParams, '&', '=', { encodeURIComponent: unescaped => unescaped })}`;
}

/**
 * Get the start and end dates from a timeFrame, currentTime, and time-zone
 * @param {string} tz - time-zone defaults to 'UTC'
 * @param {string | Date} currentTime - the time you'd like to use as a reference for the the timeFrame
 * @param {string} timeFrame - the period that defines how start and end are calculated
 * @returns {{startDt: moment, endDt: moment}}
 */
export function getTimeFrameDateRange(tz, currentTime, timeFrame) {
  const zone = tz || 'UTC';
  const today = moment.tz(currentTime, zone).startOf('day');
  const dateRange = { endDt: today };
  switch (timeFrame) {
    case 'today':
      dateRange.startDt = today;
      dateRange.endDt = moment.tz(today, zone).endOf('day');
      break;
    case 'yesterday':
      dateRange.startDt = moment.tz(today, zone).add(-1, 'd');
      dateRange.endDt = moment(dateRange.startDt).endOf('day');
      break;
    case 'lastWeek':
      dateRange.startDt = moment.tz(today, zone).add(-1, 'w');
      dateRange.endDt = moment.tz(today, zone).add(-1, 'd').endOf('day');
      break;
    case 'lastMonth':
      dateRange.startDt = moment.tz(today, zone).startOf('month').add(-1, 'M');
      dateRange.endDt = moment(dateRange.startDt).endOf('month');
      break;
    case 'thisMonth':
      dateRange.startDt = moment.tz(today, zone).startOf('month');
      break;
    case 'last30Days':
      dateRange.startDt = moment.tz(today, zone).add(-30, 'd');
      dateRange.endDt = moment.tz(today, zone).add(-1, 'd').endOf('day');
      break;
    case 'customDateRange':
      // this only is here for the client's needs. the template for customDateRange does not have
      // startDt or endDt to fill, also, would require two time entries. so the client will call this method twice
      // once for startDt and once for endDt
      dateRange.startDt = moment.tz(today, zone); // start of day
      dateRange.endDt = moment.tz(today, zone).endOf('day'); // end of day
      break;
    default:
      logger.error('%j', { fn: 'getTimeFrameDateRange', message: 'using default startDt' });
      dateRange.startDt = moment.tz(today, zone).add(-1, 'd');
      break;
  }
  // noinspection JSValidateTypes
  return dateRange;
}

function getUrlParams(tz, currentTime, timeFrame) {
  const { startDt, endDt } = getTimeFrameDateRange(tz, currentTime, timeFrame);
  return { startDt: startDt.toISOString(), endDt: endDt.toISOString() };
}

/**
 * Fill URL template variables with dynamic values
 * @param {string} urlTemplate
 * @param {string} timeFrame
 * @param {moment|string} currentTime - the time you'd like to use as a reference for the the timeFrame
 * @param {string} tz - time-zone defaults to 'UTC'
 * @returns {string} url template
 */
export function fillUrlTemplate(urlTemplate, timeFrame, currentTime, tz = 'UTC') {
  const urlParams = getUrlParams(tz, currentTime, timeFrame);
  return strformat(urlTemplate, urlParams);
}
